import React, { ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import TranslationCatalogStatusChip from '../TranslationCatalog/TranslationCatalogStatusChip';

import TranslationOverviewToolbar from './TranslationOverviewToolbar';
import { TRANSLATION_OVERVIEW_PAGE_SIZES, formatMissingFields, getUploadStateLabel } from './translationOverviewUtils';
import type { TranslationOverviewDetailRow, TranslationOverviewStatus } from './types';

interface Props {
  open: boolean;
  title: string;
  subtitle?: string;
  rowHeaderName: string;
  searchPlaceholder: string;
  rows: Array<TranslationOverviewDetailRow>;
  rowCount: number;
  isLoading: boolean;
  error?: Error | null;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  search: string;
  onSearchChange: (search: string) => void;
  status: TranslationOverviewStatus | '';
  onStatusChange: (status: TranslationOverviewStatus | '') => void;
  actions?: ReactNode;
  onClose: () => void;
}

function getColumns(rowHeaderName: string): Array<GridColDef<TranslationOverviewDetailRow>> {
  return [
    {
      field: 'label',
      headerName: rowHeaderName,
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <TranslationCatalogStatusChip status={row.status} />,
    },
    {
      field: 'uploadState',
      headerName: 'Braze',
      width: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          noWrap
          color={
            {
              Uploaded: 'success.main',
              PendingUpload: 'warning.main',
              UploadFailed: 'error.main',
              NotReady: 'text.secondary',
            }[row.uploadState]
          }
        >
          {getUploadStateLabel(row.uploadState)}
        </Typography>
      ),
    },
    {
      field: 'missingFields',
      headerName: 'Missing fields',
      flex: 1.4,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.missingFields.join(', ')} placement="top">
          <Typography variant="body2" noWrap color={row.missingFields.length ? 'text.primary' : 'text.secondary'}>
            {formatMissingFields(row.missingFields)}
          </Typography>
        </Tooltip>
      ),
    },
  ];
}

/**
 * Drill-in for a single row of either overview tab. The caller normalises its rows, so the same
 * drawer renders "the locales of this catalog" and "the catalogs of this locale".
 */
export default function TranslationOverviewDetailDrawer({
  open,
  title,
  subtitle,
  rowHeaderName,
  searchPlaceholder,
  rows,
  rowCount,
  isLoading,
  error,
  paginationModel,
  onPaginationModelChange,
  search,
  onSearchChange,
  status,
  onStatusChange,
  actions,
  onClose,
}: Props) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', md: 720 }, p: 3 } } }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h2">{title}</Typography>
          {!!subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {actions}
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Box mt={3}>
        <TranslationOverviewToolbar
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          status={status}
          onStatusChange={onStatusChange}
        />
      </Box>

      {!!error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message}
        </Alert>
      )}

      <Box mt={3} flex={1} minHeight={0}>
        <DataGrid
          rows={rows}
          columns={getColumns(rowHeaderName)}
          loading={isLoading}
          rowCount={rowCount}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={TRANSLATION_OVERVIEW_PAGE_SIZES}
          disableRowSelectionOnClick
          slots={{ pagination: GridPagination }}
          sx={{ height: '100%' }}
        />
      </Box>
    </Drawer>
  );
}
